"use client"

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import "./AdminDashboard.css"

const VideoCard = ({ video, onDelete }) => {
  const theme = useTheme();

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const videoId = getYouTubeId(video.youtube_url);
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {thumbnailUrl ? (
        <CardMedia
          component="img"
          height="140"
          image={thumbnailUrl}
          alt={video.title}
        />
      ) : (
        <Box
          sx={{
            height: 140,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.palette.grey[200],
          }}
        >
          <YouTubeIcon sx={{ fontSize: 60, color: theme.palette.grey[400] }} />
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="div" noWrap>
          {video.title}
        </Typography>
        {video.category === 'light_spot' && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Chip 
              label={video.level} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
            <Chip 
              label={`Day ${video.day_number}`} 
              size="small" 
              color="secondary" 
              variant="outlined"
            />
          </Box>
        )}
      </CardContent>
      <CardActions>
        <IconButton onClick={() => onDelete(video.id)} color="error">
          <DeleteIcon />
        </IconButton>
      </CardActions>
    </Card>
  );
};

const VideoDialog = ({ open, onClose, onSubmit, formData, setFormData, isLoading }) => {
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Video</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Video Title"
            type="text"
            fullWidth
            value={formData.title}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
          <TextField
            margin="dense"
            name="category"
            label="Category"
            select
            fullWidth
            value={formData.category}
            onChange={handleChange}
            required
            disabled={isLoading}
          >
            <MenuItem value="praise">Praise</MenuItem>
            <MenuItem value="worship">Worship</MenuItem>
            <MenuItem value="sermons">Sermons</MenuItem>
            <MenuItem value="faith">Faith</MenuItem>
            <MenuItem value="healing">Healing</MenuItem>
            <MenuItem value="prayer">Prayer</MenuItem>
            <MenuItem value="spiritual_growth">Spiritual Growth</MenuItem>
            <MenuItem value="crusade">Crusade</MenuItem>
            <MenuItem value="lightspot">Light Spot</MenuItem>
          </TextField>

          {formData.category === "lightspot" && (
            <>
              <TextField
                margin="dense"
                name="level"
                label="Level"
                select
                fullWidth
                value={formData.level}
                onChange={handleChange}
                required
                disabled={isLoading}
              >
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
              </TextField>

              <TextField
                margin="dense"
                name="day"
                label="Day Number"
                type="number"
                fullWidth
                value={formData.day}
                onChange={handleChange}
                required
                disabled={isLoading}
                inputProps={{ min: 1, max: formData.level === 'basic' ? 90 : 20 }}
              />

              <Box sx={{ mt: 2, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="hasAssignment"
                      checked={formData.hasAssignment}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                  }
                  label="Include Assignment (Optional)"
                />
              </Box>

              {formData.hasAssignment && (
                <TextField
                  margin="dense"
                  name="assignmentInstructions"
                  label="Assignment Instructions"
                  multiline
                  rows={4}
                  fullWidth
                  value={formData.assignmentInstructions}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Enter detailed instructions for the assignment..."
                />
              )}
            </>
          )}

          <TextField
            margin="dense"
            name="videoUrl"
            label="YouTube URL"
            type="url"
            fullWidth
            value={formData.videoUrl}
            onChange={handleChange}
            required
            disabled={isLoading}
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
            {isLoading ? "Uploading..." : "Upload Video"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const VideoCategories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState("praise")
  const [lightSpotLevel, setLightSpotLevel] = useState("basic")
  const [videos, setVideos] = useState({
    praise: [],
    worship: [],
    sermons: [],
    faith: [],
    healing: [],
    prayer: [],
    spiritual_growth: [],
    crusade: [],
    lightspot: {
      basic: [],
      intermediate: [],
      advanced: []
    }
  })
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    videoUrl: "",
    category: "praise",
    level: "basic",
    day: "",
    hasAssignment: false,
    assignmentInstructions: ""
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => {
    loadVideos()
  }, [activeTab, lightSpotLevel])

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("")
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadVideos = async () => {
    try {
      setIsLoading(true)
      setError("")
      
      // Initialize organized videos structure
      const organizedVideos = {
        praise: [],
        worship: [],
        sermons: [],
        faith: [],
        healing: [],
        prayer: [],
        spiritual_growth: [],
        crusade: [],
        lightspot: {
          basic: [],
          intermediate: [],
          advanced: []
        }
      }

      // Function to process a single page of videos
      const processVideosPage = (videos) => {
        videos.forEach(video => {
          console.log('Processing video:', video)
          
          // Map API categories to frontend categories
          const categoryMap = {
            'praise': 'praise',
            'worship': 'worship',
            'sermon': 'sermons',
            'faith': 'faith',
            'healing': 'healing',
            'prayer': 'prayer',
            'spiritual_growth': 'spiritual_growth',
            'crusade': 'crusade',
            'light_spot': 'lightspot'
          }
          
          const frontendCategory = categoryMap[video.category]
          console.log('Category mapping:', {
            backendCategory: video.category,
            mappedFrontendCategory: frontendCategory,
            availableCategories: Object.keys(categoryMap)
          })
          
          if (!frontendCategory) {
            console.warn(`Unknown category: ${video.category}, skipping video`)
            return
          }
          
          if (frontendCategory === 'lightspot') {
            // For Light Spot videos, handle level
            const level = video.level || 'basic'
            console.log('Light Spot level:', level)
            
            if (organizedVideos.lightspot[level]) {
              organizedVideos.lightspot[level].push(video)
              console.log(`Added Light Spot video to ${level} level`)
              console.log(`Current count in ${level}:`, organizedVideos.lightspot[level].length)
            } else {
              console.warn(`Unknown Light Spot level: ${level}, defaulting to basic`)
              organizedVideos.lightspot.basic.push(video)
            }
          } else {
            // For regular categories
            if (organizedVideos[frontendCategory]) {
              organizedVideos[frontendCategory].push(video)
              console.log(`Adding video to ${frontendCategory} category`)
              console.log(`Current count in ${frontendCategory}:`, organizedVideos[frontendCategory].length)
            } else {
              console.warn(`Category ${frontendCategory} not found in organizedVideos`)
            }
          }
        })
      }

      // Fetch all pages of videos
      let nextPage = null
      let currentPage = 1
      
      do {
        console.log(`Fetching page ${currentPage} of videos`)
        const response = await apiService.getVideos(nextPage)
        console.log(`Raw API Response for page ${currentPage}:`, response)
        
        // Process the current page of videos
        processVideosPage(response.results)
        
        // Update next page URL
        nextPage = response.next
        currentPage++
      } while (nextPage)

      console.log('Final organized videos structure:', organizedVideos)
      setVideos(organizedVideos)
    } catch (error) {
      console.error('Error loading videos:', error)
      setError(error.message || 'Failed to load videos')
    } finally {
      setIsLoading(false)
    }
  }

  // Add useEffect to log videos state changes
  useEffect(() => {
    console.log('Current videos state:', videos)
  }, [videos])

  const standardizeYouTubeUrl = (url) => {
    try {
      if (!url) {
        console.error('Empty URL provided');
        return '';
      }
      
      // Remove any whitespace
      url = url.trim();
      console.log('Original URL:', url);
      
      // Handle different YouTube URL formats
      let videoId = '';
      
      try {
        // Handle youtu.be links
        if (url.includes('youtu.be/')) {
          videoId = url.split('youtu.be/')[1];
          console.log('Extracted videoId from youtu.be:', videoId);
        }
        // Handle youtube.com/watch?v= links
        else if (url.includes('youtube.com/watch')) {
          const urlObj = new URL(url);
          videoId = urlObj.searchParams.get('v');
          console.log('Extracted videoId from youtube.com/watch:', videoId);
        }
        // Handle youtube.com/embed/ links
        else if (url.includes('youtube.com/embed/')) {
          videoId = url.split('youtube.com/embed/')[1];
          console.log('Extracted videoId from youtube.com/embed:', videoId);
        }
        // Handle youtube.com/v/ links
        else if (url.includes('youtube.com/v/')) {
          videoId = url.split('youtube.com/v/')[1];
          console.log('Extracted videoId from youtube.com/v:', videoId);
        }
        // Handle youtube.com/live/ links
        else if (url.includes('youtube.com/live/')) {
          videoId = url.split('youtube.com/live/')[1];
          console.log('Extracted videoId from youtube.com/live:', videoId);
        }
        
        // Remove any additional parameters after the video ID
        if (videoId) {
          videoId = videoId.split('&')[0];
          videoId = videoId.split('?')[0]; // Remove query parameters
          const standardizedUrl = `https://www.youtube.com/watch?v=${videoId}`;
          console.log('Standardized URL:', standardizedUrl);
          return standardizedUrl;
        }
        
        console.warn('Could not extract videoId from URL:', url);
        return url;
      } catch (error) {
        console.error('Error processing URL:', error);
        return url;
      }
    } catch (error) {
      console.error('Error in standardizeYouTubeUrl:', error);
      return url;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setIsLoading(true)
      setError("")

      const videoData = {
        title: formData.title,
        category: formData.category,
      }

      if (formData.category === 'lightspot') {
        // Only validate day number for Light Spot videos
        if (!formData.day) {
          throw new Error('Day number is required for Light Spot videos')
        }
        videoData.level = formData.level
        videoData.day_number = parseInt(formData.day)
        
        // Add assignment data if checkbox is checked
        if (formData.hasAssignment) {
          videoData.has_assignment = true;
          videoData.assignment_instructions = formData.assignmentInstructions;
        } else {
          videoData.has_assignment = false;
        }
      }

      // Validate and standardize YouTube URL
      const youtubeUrl = formData.videoUrl.trim()
      console.log('Original YouTube URL:', youtubeUrl);
      
      if (!youtubeUrl.match(/^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/)) {
        throw new Error('Please enter a valid YouTube URL')
      }
      
      // Standardize the YouTube URL
      const standardizedUrl = standardizeYouTubeUrl(youtubeUrl)
      console.log('Final standardized URL:', standardizedUrl)
      
      if (!standardizedUrl.includes('youtube.com/watch?v=')) {
        throw new Error('Invalid YouTube URL format after standardization')
      }
      
      videoData.youtube_url = standardizedUrl

      console.log('Submitting video data:', videoData)
      const response = await apiService.createVideo(videoData)
      console.log('Video created response:', response)

      setSuccessMessage("Video uploaded successfully!")
      setFormData({
        title: "",
        videoUrl: "",
        category: "praise",
        level: "basic",
        day: "",
        hasAssignment: false,
        assignmentInstructions: ""
      })
      setShowForm(false)

      // Refresh the video list
      await loadVideos()

      // If it's a Light Spot video, switch to the Light Spot tab and level
      if (formData.category === 'lightspot') {
        setActiveTab('lightspot')
        setLightSpotLevel(formData.level)
      }
    } catch (error) {
      console.error('Error uploading video:', error)
      setError(error.message || 'Failed to upload video')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      try {
        setIsLoading(true)
        setError("")
        await apiService.deleteVideo(id)
        setSuccessMessage("Video deleted successfully!")
        loadVideos()
      } catch (error) {
        console.error('Error deleting video:', error)
        setError(error.message || 'Failed to delete video')
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Video Categories
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setShowForm(true)}
        >
          Add Video
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Regular Categories */}
          {Object.entries(videos).map(([category, categoryVideos]) => {
            // Skip lightspot category as it's handled separately
            if (category === 'lightspot') return null;
            
            // Skip empty categories
            if (!Array.isArray(categoryVideos) || categoryVideos.length === 0) return null;

            console.log(`Rendering category: ${category} with ${categoryVideos.length} videos`);

            return (
              <Box key={category}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </Typography>
                <Grid container spacing={isMobile ? 1 : 3}>
                  {categoryVideos.map((video) => (
                    <Grid item xs={12} sm={6} md={4} key={video.id}>
                      <VideoCard
                        video={video}
                        onDelete={handleDelete}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}

          {/* Light Spot Category */}
          {videos.lightspot && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Light Spot
              </Typography>
              {Object.entries(videos.lightspot).map(([level, levelVideos]) => {
                // Skip empty levels
                if (!Array.isArray(levelVideos) || levelVideos.length === 0) return null;

                console.log(`Rendering Light Spot level: ${level} with ${levelVideos.length} videos`);

                return (
                  <Box key={level} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {level.charAt(0).toUpperCase() + level.slice(1)} Level
                    </Typography>
                    <Grid container spacing={isMobile ? 1 : 3}>
                      {levelVideos.map((video) => (
                        <Grid item xs={12} sm={6} md={4} key={video.id}>
                          <VideoCard
                            video={video}
                            onDelete={handleDelete}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      <VideoDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        isLoading={isLoading}
      />
    </Box>
  )
}

const styles = `
  .video-categories-container {
    padding: 20px;
    max-width: 1400px;
    margin: 0 auto;
  }

  .video-categories-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
  }

  .video-categories-grid {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  .video-category-section {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .category-title {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid #eee;
  }

  .video-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    padding: 10px;
  }

  .video-card {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: transform 0.2s;
  }

  .video-card:hover {
    transform: translateY(-2px);
  }

  .video-thumbnail {
    width: 100%;
    height: 180px;
    overflow: hidden;
    position: relative;
  }

  .video-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .thumbnail-placeholder {
    width: 100%;
    height: 100%;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
  }

  .video-info {
    padding: 15px;
  }

  .video-title {
    font-size: 1rem;
    margin: 0 0 10px 0;
    color: #333;
    line-height: 1.4;
    height: 2.8em;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .video-meta {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
  }

  .level-badge, .day-badge {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    background: #e3f2fd;
    color: #1976d2;
  }

  .video-actions {
    display: flex;
    gap: 10px;
    margin-top: 10px;
  }

  .edit-btn, .delete-btn {
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    transition: background-color 0.2s;
  }

  .edit-btn {
    background: #e3f2fd;
    color: #1976d2;
  }

  .edit-btn:hover {
    background: #bbdefb;
  }

  .delete-btn {
    background: #ffebee;
    color: #d32f2f;
  }

  .delete-btn:hover {
    background: #ffcdd2;
  }

  .add-video-btn {
    padding: 10px 20px;
    background: #1976d2;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    transition: background-color 0.2s;
  }

  .add-video-btn:hover {
    background: #1565c0;
  }

  @media (max-width: 768px) {
    .video-grid {
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    }
  }

  .level-section {
    margin-bottom: 30px;
  }

  .level-title {
    font-size: 1.2rem;
    color: #666;
    margin: 20px 0 15px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
  }

  .level-section:first-child .level-title {
    margin-top: 0;
  }
`;

// Add the styles to the document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default VideoCategories